import { LinkWordmark } from "@taunggyi-plus/design-system";

type Chapter = { title: string; steps: string[] };
const customer: Chapter[] = [
  {
    title: "Find a service",
    steps: [
      "Open Services and choose hotels, food, delivery or taxi.",
      "Use the filter button to narrow the list. Open a provider to check its details and availability.",
      "Use the bookmark button to save a provider. Find saved providers under More → Favourites.",
    ],
  },
  {
    title: "Book or place an order",
    steps: [
      "Open the service you want and fill in the booking or order form. Check dates, quantities, locations and contact details before submitting.",
      "For delivery, enter pickup, drop-off and recipient details. Check any quoted delivery fee before accepting it.",
      "For taxi, choose an available verified driver. Use the in-app call button to contact the driver and allow microphone access when prompted. Confirm the fare with the driver before starting the ride.",
      "Open Orders to check the latest status. A submitted request may still need the provider’s confirmation.",
    ],
  },
  {
    title: "Track orders and request help",
    steps: [
      "Open Orders and select View on the relevant record to see its details.",
      "Use the support icon beside the list controls. Choose order support, refund request or review, select the relevant order and enter your message.",
      "Open the Request tab to follow submitted requests and read responses. A refund request is reviewed before a refund is recorded.",
    ],
  },
  {
    title: "Review a completed service",
    steps: [
      "Complete the service before submitting a review.",
      "Open the support form and choose Review, or use Write a review on an eligible provider page.",
      "Select the completed order, choose a rating from one to five and describe your experience. Submit the review.",
      "Open More → Reviews to find your submitted reviews.",
    ],
  },
  {
    title: "Browse events",
    steps: [
      "Open Events to browse the available event cards.",
      "Open an event to read its dates, location and participation information. Follow the actions offered on that event.",
    ],
  },
];
const provider: Chapter[] = [
  {
    title: "Check incoming work",
    steps: [
      "Start on Dashboard to check current work and items needing attention.",
      "Open Orders, use filters to find the work you need and select View for details.",
      "Read the customer’s requirements before accepting or updating an order. Use the actions available for its current status.",
      "Update progress as the work happens. Mark a service complete only after it has been delivered.",
    ],
  },
  {
    title: "Keep service details current",
    steps: [
      "Open More to find your rooms, products, riders or taxi data, depending on your app.",
      "Use Add to create a record and open an existing record to edit it. Complete the required fields before saving.",
      "Keep opening hours, availability, prices and service information accurate. Use Categories & tags where available.",
    ],
  },
  {
    title: "Subscriptions and records",
    steps: [
      "Open More → Subscription to review your subscription information. Follow the available renewal or payment-request actions.",
      "Delivery riders are covered by their owner’s subscription.",
      "Use Finance to review recorded fees, receipts and expenses. Use filters to select the period or records you need. Recorded differences are not automatically profit.",
    ],
  },
];
const admin: Chapter[] = [
  {
    title: "Manage partners and accounts",
    steps: [
      "Use Dashboard to review work needing attention. Open Partners and use filters to locate an organization.",
      "Select View, then the relevant management action to review its service information or subscription.",
      "Use More → Taxi Driver List to create driver accounts and manage verification. Customers see drivers who meet the account, verification and availability requirements.",
      "Manage delivery owners and rider accounts from the available lists. The default owner rider limit is ten; only Admin can change that limit.",
    ],
  },
  {
    title: "Manage orders and customer requests",
    steps: [
      "Open Orders to review bookings and orders across services. Filter by service or status and select View for the full record.",
      "Use Assign delivery fleets when a delivery needs assignment.",
      "Open More → Order support & refunds to review customer cases. Check the order and message before recording a response or refund information.",
    ],
  },
  {
    title: "Record finances",
    steps: [
      "Open Finance and use Add to create a supported finance record. Check the organization, category, amount and reference before saving.",
      "Use Filter to choose the period and record types. Refresh reloads the current report.",
      "Open a finance record to inspect its details. Fees, receipts and expenses are separate records; a recorded fee does not by itself mean payment was received.",
    ],
  },
  {
    title: "Maintain platform information",
    steps: [
      "Use More to manage categories, tags, promotions, events, subscriptions and team access.",
      "Review pending applications and payment requests using their dedicated pages before approving them.",
      "Use Custom notifications for platform announcements and Audit for available activity records. Grant team access according to each person’s responsibilities.",
    ],
  },
];

export function AppManual({ role }: { role: string }) {
  const chapters: Chapter[] = [
    {
      title: "Get started",
      steps: [
        "Sign in to the app for your role. Accounts and sessions are separate in each app, so you can stay signed in to more than one app.",
        "Use the top navigation on desktop or the bottom navigation on your phone. More contains your profile and account tools.",
        "Use the globe icon to choose a language. Use More → Add to Home Screen to install a shortcut when your browser supports it.",
      ],
    },
    ...(role === "customer" ? customer : role === "admin" ? admin : provider),
    ...(role === "taxi"
      ? [
          {
            title: "Use your driver account",
            steps: [
              "Admin creates your driver account. Sign in with the account provided to you.",
              "Check your vehicle details and availability before taking requests.",
              "Keep the taxi app available for incoming customer calls. Allow microphone access for in-app calls and confirm the fare with the customer.",
              "Review incoming ride orders and update ride progress using the actions shown in the order.",
            ],
          },
        ]
      : []),
    ...(role === "delivery-owner"
      ? [
          {
            title: "Manage your riders",
            steps: [
              "Open More → Rider accounts to manage riders under your organization.",
              "Your subscription covers your riders. You can create up to the limit assigned to your account; the default is ten.",
              "Ask Admin to change the limit if you need more rider accounts. Review assignments and keep rider account details current.",
            ],
          },
        ]
      : []),
    {
      title: "Notifications and list controls",
      steps: [
        "Open the bell icon to read notifications. Select a notification to open its related detail page.",
        "Use Mark all as read to clear unread indicators and See more to open the full notification list.",
        "Enable device alerts when you want browser notifications, then allow permission in your browser. Enable order alert sound separately if that option is shown.",
        "Use the filter icon to narrow records. Lists load more records as you scroll; use Load more when offered. Use Back to records from a detail page to return to the list.",
      ],
    },
    {
      title: "If something does not work",
      steps: [
        "If a page cannot connect, check your internet connection and use Try again or Refresh.",
        "If a call has no audio, check microphone permission for this app in your browser and device settings.",
        "If device alerts do not arrive, check browser notification permission and device notification settings. The in-app bell remains available to check notices.",
        "If an action is unavailable, check the record’s status and your account’s access. Keep the order or record reference when asking an administrator for help.",
        "On a shared device, use More → Sign out when finished.",
      ],
    },
  ];
  return (
    <article className="app-manual">
      <a className="more-back-link" href="/more">
        ← More
      </a>
      <header>
        <h1 id="more-page-title">
          How to use <LinkWordmark />
        </h1>
        <p>A step-by-step guide for the {role.replaceAll("-", " ")} app.</p>
      </header>
      <nav className="app-manual__contents" aria-label="Guide contents">
        {chapters.map((c, i) => (
          <a key={c.title} href={`#guide-${i}`}>
            {i + 1}. {c.title}
          </a>
        ))}
      </nav>
      {chapters.map((c, i) => (
        <section className="card" id={`guide-${i}`} key={c.title}>
          <h3>
            {i + 1}. {c.title}
          </h3>
          <ol>
            {c.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </section>
      ))}
    </article>
  );
}
